import React, { useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  CreateUpdateTeamDto,
  GetTeamDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { TeamModalEditor } from '../../components/Teams/TeamModalEditor';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import {
  convert,
  validate,
} from '../../../common/pokCore/contexts/TeamContext';
import { TeamsSearch } from '../../components/Teams/TeamSearch';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';

export const TeamsView: React.FC = () => {
  const pok = usePokCore();
  const [team, setTeam] = useState<GetTeamDto>();
  const [showNew, setShowNew] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const { t, tk } = useTranslation('administrationLng');

  const handleAdd = () => {
    setShowNew(true);
    setTeam(undefined);
  };

  const addEditTeam = async (teamCU: CreateUpdateTeamDto, id?: string) => {
    const status = await validate(teamCU);
    if (status.valid) {
      try {
        if (id) {
          await pok.teams.update(id, teamCU);
        } else {
          await pok.teams.create(teamCU);
        }
        setShowNew(false);
        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleCloseNew = () => {
    setShowNew(false);
  };

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.teams.getAllByPortion(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.teams],
  );

  const getCount = useMemo(
    () =>
      ((filterText: string, filterColumns?: string) =>
        pok.teams.getAllCount(filterText, filterColumns)) as GridCountFunction,
    [pok.teams],
  );

  const handleClick = async (id: string) => {
    const getTeam = await pok.teams.getById(id);
    setTeam(getTeam);
    setShowNew(true);
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.common.teams)}</Card.Title>
        <Button variant="outline-primary" onClick={handleAdd}>
          {t(tk.administration.teamNew)}
        </Button>
        <TeamsSearch
          getData={getData}
          getCount={getCount}
          onRowClick={handleClick}
          refresh={refresh}
        />
        <TeamModalEditor
          id={team?.id}
          systemNumber={team?.systemNumber}
          team={convert(team)}
          onCompleted={addEditTeam}
          show={showNew}
          handleClose={handleCloseNew}
        />
      </Card.Body>
    </Card>
  );
};
